import { type FC, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBlockActions } from '@ContractBuilder/modules/block-edit/hooks/use-block-actions';
import { useEntityStore } from '@ContractBuilder/store/entity.store';
import type { CreateBlockPayloadInput } from '@domain/schemas/payload.schema';
import { blockQueries } from '@queries/fetchBlocks';
import Nav from '@root/src/routes';
import { isClausesPath } from '@root/src/utils/app-paths';
import { useQueryClient } from '@tanstack/react-query';

import type { CreateLibraryBlockFormValues } from '../types';
import { CreateLibraryBlockModal } from '../views/CreateLibraryBlockModal';

interface CreateLibraryBlockControllerProps {
  handleClose: () => void;
}

export const CreateLibraryBlockController: FC<CreateLibraryBlockControllerProps> = ({ handleClose }) => {
  const isClauses = isClausesPath();

  const isCreating = useEntityStore((state) => state.isLoading);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createBlockInLibrary } = useBlockActions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSubmit = async (data: CreateLibraryBlockFormValues) => {
    setIsSubmitting(true);
    try {
      const payload: CreateBlockPayloadInput = {
        ...data,
        content: '',
        type: isClauses ? 'clause' : 'mrc-heading',
      };

      const id = await createBlockInLibrary(payload);
      await queryClient.invalidateQueries({ queryKey: blockQueries.list().queryKey });

      const basePath = isClauses ? Nav.Clause : Nav.Block;
      toast.success(`${isClauses ? 'Clause' : 'Block'} has been successfully created`);
      return navigate(generatePath(basePath, { id }));
    } catch (_error) {
      toast.error(`Unable to create ${isClauses ? 'clause' : 'block'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CreateLibraryBlockModal
      isLoading={isCreating || isSubmitting}
      onClose={handleClose}
      type={isClauses ? 'clause' : 'block'}
      onSubmit={handleSubmit}
    />
  );
};
